import React from 'react';
import { IStore } from 'Store/.';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Dispatch, bindActionCreators } from 'redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { fetchCurrentUser, fetchLogout } from 'Store/User/thunks';
import { OrderPage, PaymentStatus } from 'Store/Order/enums';
import { setCustomerTermsApproval, setFetchPaymentFailed } from 'Store/Order/actions';
import { OrderItemsIDs } from 'Components/OrderItem/Redux/reducer';
import OrderPaymentPage from 'Pages/OrderPaymentPage/OrderPaymentPage';
import FormattedText from 'Components/Shared/FormattedText/FormattedText';
import WrapperBox from 'Components/Shared/WrapperBox/WrapperBox';

import { 
  getOrderDetails, 
  getIsTermsApproved,
  getIsPaymentSelected,
  getPaymentReturnUrl,
  getPaymentStatus,
  getIsOrderItemsEmpty,
  getIsFetchingOrderOrPayment,
  getIsFetchOrderOrPaymentFailed,
  getIsFetchPaymentFailed,
  getIsFetchOrderFailed,
} from 'Store/Order/selectors';

import { 
  fetchOrderSummary, 
  fetchPaymentResult,
  fetchCurrentOrder,
  fetchClearOrder,
} from 'Store/Order/thunks';
import PaymentFailedModal from './PaymentFailedModal/PaymentFailedModal';

interface IPropsInternal {
  isFetching: boolean;
  isFetchOrderFailed: boolean;
  isFetchPaymentFailed: boolean;
  isTermsApproved: boolean;
  isPaymentSet: boolean;
  isOrderEmpty: boolean;
  paymentStatus: PaymentStatus;
  paymentUrl: string;
  orderDetails: Julius.Website.OrderDetailsViewModel;
  setCustomerTermsApproval: (isApproved: boolean) => void;
  fetchCurrentUser: () => void;
  fetchCurrentOrder: () => void;
  fetchOrderSummary: (orderDetails: Julius.Website.OrderDetailsViewModel) => void;
  fetchPaymentResult: (returnPath: string) => void;
  setFetchPaymentFailed: (isFailed: boolean) => void;
  fetchClearOrder: () => void;
  fetchLogout: () => void;
}

type Props = IPropsInternal & RouteComponentProps<{ orderItemID: string }>;

interface IState {
  isShowingWarning: boolean;
  isShowingFailedModal: boolean;
}

class OrderPaymentPageContainer extends React.PureComponent<Props, IState> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isShowingWarning: false,
      isShowingFailedModal: false,
    }
  }

  componentDidMount() {
    this.props.fetchCurrentUser();

    const { orderDetails } = this.props;
    if (orderDetails) {
      this.props.fetchOrderSummary(orderDetails);
    } else {
      this.props.fetchCurrentOrder();
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    const { paymentUrl } = nextProps;

    if (nextProps.isFetchPaymentFailed) {
      this.handlePaymentFailed();
      return;
    }

    if (paymentUrl && paymentUrl !== this.props.paymentUrl) {
      this.goToPaymentUrl(paymentUrl);
      return;
    }

    const { isShowingWarning } = this.state;
    const { isTermsApproved, isPaymentSet } = nextProps;

    if (isShowingWarning && isTermsApproved && isPaymentSet) {
      this.setState({ isShowingWarning: false });
    } 
  }

  handlePaymentFailed = () => {
    // Payment failure may be due to a bug that has to do with 
    // mixed up customer IDs in the backend. To prevent the wrong 
    // user from getting the wrong order, the order needs to step 
    // back one stage and the user has to log in again.
    this.setState({ isShowingFailedModal: true });
    this.props.fetchLogout();
  }

  onBack = () => {
    this.redirectTo(OrderPage.DETAILS);
  }

  onNext = () => {
    const { isTermsApproved, isPaymentSet } = this.props;

    if (isTermsApproved && isPaymentSet) {
      this.doPayment();
    } else {
      this.setState({ isShowingWarning: true });
    }
  }

  doPayment = () => {
    const { orderItemID } = this.props.match.params;
    const returnPath = `/order/${ orderItemID }${ OrderPage.CONFIRMATION }`;
    this.props.fetchPaymentResult(returnPath);
  }

  goToPaymentUrl = (url: string) => {
    window.location.replace(url);
  }

  isOrderItemVoucher = () => {
    const { orderItemID } = this.props.match.params;
    return orderItemID === OrderItemsIDs.VOUCHER;
  }

  redirectTo = (page: string) => {
    const { history, match: { params: { orderItemID } } } = this.props;
    history.replace(`/order/${ orderItemID }${ page }`);
  }

  redirectToStartPage = () => {
    this.props.fetchClearOrder();
    this.props.history.replace('/');
  }

  handleRetryAfterFailure = () => {
    this.props.setFetchPaymentFailed(false);
    this.redirectTo(OrderPage.DETAILS)
  }

  render() {

    const { paymentStatus } = this.props;

    if (paymentStatus === PaymentStatus.PAID) {
      return (
        <WrapperBox>
          <FormattedText 
            displayName='Betalning genomförs...'
            formattedName='order.payment.complete'
          />
        </WrapperBox>
      );
    }

    const isFetching = this.props.isFetching || 
      paymentStatus === PaymentStatus.SENT_TO_PAYMENT_SYSTEM;

    return (
      <>
        <PaymentFailedModal
          isVisible={ this.state.isShowingFailedModal }
          onCancel={ this.redirectToStartPage }
          onTryAgain={ this.handleRetryAfterFailure }
        />
        <OrderPaymentPage 
          isFetching={ isFetching }
          isFetchFailed={ this.props.isFetchOrderFailed }
          isShowingWarning={ this.state.isShowingWarning }
          isPaymentSet={ this.props.isPaymentSet }
          isOrderEmpty={ this.props.isOrderEmpty }
          onCustomerTermsChange={ this.props.setCustomerTermsApproval }
          onNext={ this.onNext }
          onBack={ this.onBack }
        />
      </>
    );
  }
}

const mapStateToProps = (state: IStore) => {
  return {
    isFetching: getIsFetchingOrderOrPayment(state),
    isFetchOrderFailed: getIsFetchOrderFailed(state),
    isFetchPaymentFailed: getIsFetchPaymentFailed(state),
    isTermsApproved: getIsTermsApproved(state),
    isPaymentSet: getIsPaymentSelected(state),
    isOrderEmpty: getIsOrderItemsEmpty(state),
    paymentStatus: getPaymentStatus(state),
    paymentUrl: getPaymentReturnUrl(state),
    orderDetails: getOrderDetails(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return bindActionCreators({
    fetchLogout,
    fetchClearOrder,
    fetchCurrentUser,
    fetchCurrentOrder,
    fetchOrderSummary,
    fetchPaymentResult,
    setCustomerTermsApproval,
    setFetchPaymentFailed,
  }, dispatch);
};

export default compose<{}, {}>(
  withRouter,
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps),
)(OrderPaymentPageContainer);


// WEBPACK FOOTER //
// ./src/pages/OrderPaymentPage/OrderPaymentPageContainer.tsx